import { useState } from "react";
import { H5 } from "@datapay-by-datacom/design-system";
import { ThreadSummary } from "../../generated/core-api";
import { useParams } from "react-router";
import { ThreadContextMenu } from "./ThreadContextMenu";
import { useThreadList } from "../hooks";
import { useWorkspace } from "../../workspace";
import s from "./ThreadSelector.module.css";

interface ThreadSelectorProps {
  onSelect: (thread: ThreadSummary) => void;
}

// API returns UTC timestamps without a Z suffix, so append Z to ensure
// correct parsing as UTC.
function parseUtcDate(timestamp: string): Date {
  return new Date(timestamp.endsWith("Z") ? timestamp : timestamp + "Z");
}

export function ThreadSelector({ onSelect }: ThreadSelectorProps) {
  const workspaceId = useWorkspace().workspace_id;
  const params = useParams();
  const threadList = useThreadList(workspaceId);
  const [search, setSearch] = useState("");

  // Sort threads by creation date (newest first)
  const sortedThreads = [...threadList.data].sort(
    (a, b) =>
      parseUtcDate(b.created_at).valueOf() -
      parseUtcDate(a.created_at).valueOf()
  );

  // Filter by search term
  const filteredThreads = search
    ? sortedThreads.filter((thread) =>
        thread.title.toLowerCase().includes(search.toLowerCase())
      )
    : sortedThreads;

  // Partition threads into today and previous in a single pass
  const todayDate = new Date().toLocaleDateString();
  const { todayThreads, previousThreads } = filteredThreads.reduce(
    (acc, thread) => {
      if (parseUtcDate(thread.created_at).toLocaleDateString() === todayDate) {
        acc.todayThreads.push(thread);
      } else {
        acc.previousThreads.push(thread);
      }
      return acc;
    },
    {
      todayThreads: [] as ThreadSummary[],
      previousThreads: [] as ThreadSummary[],
    },
  );

  const hasThreads = sortedThreads.length > 0;

  return (
    <div className={s.threadSelector}>
      <div className={s.threadsContainer}>
        {hasThreads && (
          <input
            type="text"
            className={s.searchInput}
            placeholder="Search threads..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        )}

        {/* Today section - only shown if there are threads from today */}
        {todayThreads.length > 0 && (
          <>
            <H5>Today</H5>
            <div className={s.threadList}>
              {todayThreads.map((thread) => (
                <div
                  key={thread.thread_id}
                  className={`${s.threadItem} ${
                    thread.thread_id === params.threadId ? s.active : ""
                  }`}
                  onClick={() => {
                    onSelect(thread);
                  }}
                >
                  <div className={s.threadItemText}>{thread.title}</div>
                  <ThreadContextMenu thread={thread} />
                </div>
              ))}
            </div>
          </>
        )}

        {/* Previous section - only shown if there are threads from before today */}
        {previousThreads.length > 0 && (
          <>
            <H5>Previous</H5>
            <div className={s.threadList}>
              {previousThreads.map((thread) => (
                <div
                  key={thread.thread_id}
                  className={`${s.threadItem} ${
                    thread.thread_id === params.threadId ? s.active : ""
                  }`}
                  onClick={() => {
                    onSelect(thread);
                  }}
                >
                  <div className={s.threadItemText}>{thread.title}</div>
                  <ThreadContextMenu thread={thread} />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
